// Aufgaben und Follow-ups: Zeile (erledigen, verschieben, Text ändern, Priorität), „+ Follow-up“ (ein Klick = morgen,
// Uhrzeit nur wenn gewählt, im Formular auch als Anrufaufgabe) und Datumswahl. Genutzt von der Inbox und der Lead-Seite.
// Close-Systemaufgaben (Anruf, Voicemail, SMS, Opportunity fällig): Typ-Kreis, Text aus dem Backend, nur Erledigen,
// bei Calls mit Rückruf-Knopf (components/inbox/). Anrufaufgaben (Close „Call“) sind wie Follow-ups änderbar und haben
// einen Anrufen-Knopf; sie erledigen sich nach dem Gespräch (Server).
// Verschieben: Uhr-Symbol öffnet Schnellwahlen mit Datum, Monatskalender und Uhrzeit (components/inbox/Verschieben.tsx).
// Priorität Hoch/Normal/Niedrig: Hoch = Close-Haken „High Priority“ (rotes Doppel-Chevron), Niedrig nur im CRM.
import { useRef, useState } from "react";
import { aufgabenApi, type Aufgabe, type AufgabeEingabe, type Kontakt, type Prioritaet } from "../api";
import { useAuth } from "../auth/AuthContext";
import { useMeta } from "../daten/meta";
import { berlinTeile, berlinZuIso, faelligkeit, gueltig, heute, tagPlus, tagText } from "../werkzeuge/zeit";
import { Ausklapp } from "./Ausklapp";
import { Auswahl } from "./Auswahl";
import { Avatar } from "./Bausteine";
import { pickerOeffnen } from "./FeldEingabe";
import { ZeitFeld } from "./ZeitFeld";
import { Icon } from "./Icon";
import { useMeldung } from "./Meldungen";
import { MenueKnopf, type MenuePunkt } from "./Menue";
import { LeadZelle } from "./inbox/LeadZelle";
import { useRueckruf } from "./inbox/rueckruf";
import { ART_LABEL, TypSymbol } from "./inbox/TypKreis";
import { anzeigeText, istAnrufaufgabe, istAnrufbar, istSystem, mitArt } from "./inbox/typen";
import { VerschiebenPopover } from "./inbox/Verschieben";
import { telefonText } from "../werkzeuge/telefon";
import "../styles/lead.css";

export const PRIORITAETEN: { wert: Prioritaet; label: string }[] = [
  { wert: "hoch", label: "Hoch" },
  { wert: "normal", label: "Normal" },
  { wert: "niedrig", label: "Niedrig" },
];
const PRIO_RANG: Record<Prioritaet, number> = { hoch: 0, normal: 1, niedrig: 2 };

/** 0 hoch, 1 normal, 2 niedrig (fehlt das Feld: normal) */
export function prioritaetRang(a: Pick<Aufgabe, "prioritaet">): number {
  return PRIO_RANG[a.prioritaet ?? "normal"] ?? 1;
}

/** Sortierung wie in der Inbox: Hoch zuerst, Niedrig zuletzt, innerhalb nach Fälligkeit (ohne Datum zuletzt). */
export function aufgabenSortieren(liste: Aufgabe[]): Aufgabe[] {
  return [...liste].sort(
    (x, y) => prioritaetRang(x) - prioritaetRang(y) || (x.due_at ?? "9999").localeCompare(y.due_at ?? "9999"),
  );
}

/** Marke vor dem Aufgabentext: Hoch = rotes Doppel-Chevron (wie Close), Niedrig = grauer Pfeil nach unten. */
export function PrioritaetMarke({ prioritaet, groesse = 14, mitText = false }: { prioritaet?: Prioritaet | null; groesse?: number; mitText?: boolean }) {
  if (prioritaet !== "hoch" && prioritaet !== "niedrig") return null;
  const hoch = prioritaet === "hoch";
  const label = hoch ? "Hohe Priorität" : "Niedrige Priorität";
  return (
    <span className={`prio prio--${prioritaet}${mitText ? " prio--abzeichen" : ""}`} title={label} aria-label={label} role="img">
      <svg width={groesse} height={groesse} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {hoch ? <path d="M6 12l6-6 6 6M6 19l6-6 6 6" /> : <path d="M7 10l5 5 5-5" />}
      </svg>
      {mitText ? <span className="prio__text">{hoch ? "Hoch" : "Niedrig"}</span> : null}
    </span>
  );
}

/** Drei Knöpfe Hoch / Normal / Niedrig (Anlegen und Ändern). */
export function PrioritaetWahl({ wert, onWahl, klein = false }: { wert: Prioritaet; onWahl: (p: Prioritaet) => void; klein?: boolean }) {
  return (
    <div className="prio-wahl" role="radiogroup" aria-label="Priorität">
      {PRIORITAETEN.map((p) => (
        <button
          key={p.wert}
          type="button"
          role="radio"
          aria-checked={wert === p.wert}
          className={`wahlknopf${klein ? " wahlknopf--klein" : ""}${wert === p.wert ? " wahlknopf--an" : ""} prio-wahl__knopf prio-wahl__knopf--${p.wert}`}
          onClick={() => onWahl(p.wert)}
        >
          {p.wert !== "normal" ? <PrioritaetMarke prioritaet={p.wert} groesse={12} /> : null}
          {p.label}
        </button>
      ))}
    </div>
  );
}

/** Neues Datum einer Aufgabe; eine vorhandene Uhrzeit wird mitgenommen. */
export function verschobenesDatum(a: Pick<Aufgabe, "date">, tag: string): string {
  if (a.date && a.date.length > 10 && gueltig(a.date)) return berlinZuIso(tag, berlinTeile(a.date).zeit);
  return tag;
}

export function useAufgabenAktionen() {
  const meldung = useMeldung();
  return {
    async erledigen(a: Aufgabe, erledigt = true): Promise<Aufgabe | null> {
      try {
        const r = await aufgabenApi.aendern(a.id, { is_complete: erledigt });
        meldung.ok(erledigt ? `Erledigt: ${anzeigeText(a)}` : "Wieder offen.", {
          label: "Rückgängig",
          onClick: () => void aufgabenApi.aendern(a.id, { is_complete: !erledigt }).catch((e) => meldung.fehler(e, "Nicht rückgängig gemacht")),
        });
        return r.task;
      } catch (e) {
        meldung.fehler(e, "Nicht gespeichert");
        return null;
      }
    },
    async verschieben(a: Aufgabe, tag: string): Promise<Aufgabe | null> {
      try {
        const r = await aufgabenApi.aendern(a.id, { date: verschobenesDatum(a, tag) });
        meldung.ok(`Verschoben auf ${tagText(tag)}.`, a.date ? { label: "Rückgängig", onClick: () => void aufgabenApi.aendern(a.id, { date: a.date as string }).catch((e) => meldung.fehler(e, "Nicht rückgängig gemacht")) } : undefined);
        return r.task;
      } catch (e) {
        meldung.fehler(e, "Nicht verschoben");
        return null;
      }
    },
    /** Verschieben mit fertigem Wert aus dem Verschieben-Menü: Datum oder ISO mit Uhrzeit */
    async verschiebenAuf(a: Aufgabe, date: string): Promise<Aufgabe | null> {
      try {
        const r = await aufgabenApi.aendern(a.id, { date });
        const t = date.length > 10 && gueltig(date) ? berlinTeile(date) : { datum: date.slice(0, 10), zeit: "" };
        meldung.ok(`Verschoben auf ${tagText(t.datum)}${t.zeit ? `, ${t.zeit} Uhr` : ""}.`, a.date ? { label: "Rückgängig", onClick: () => void aufgabenApi.aendern(a.id, { date: a.date as string }).catch((e) => meldung.fehler(e, "Nicht rückgängig gemacht")) } : undefined);
        return r.task;
      } catch (e) {
        meldung.fehler(e, "Nicht verschoben");
        return null;
      }
    },
    async datumSetzen(a: Aufgabe, date: string): Promise<Aufgabe | null> {
      try {
        const r = await aufgabenApi.aendern(a.id, { date });
        return r.task;
      } catch (e) {
        meldung.fehler(e, "Datum nicht gespeichert");
        return null;
      }
    },
    async textSetzen(a: Aufgabe, text: string): Promise<Aufgabe | null> {
      try {
        const r = await aufgabenApi.aendern(a.id, { text });
        return r.task;
      } catch (e) {
        meldung.fehler(e, "Text nicht gespeichert");
        return null;
      }
    },
    async prioritaetSetzen(a: Aufgabe, prioritaet: Prioritaet): Promise<Aufgabe | null> {
      try {
        const r = await aufgabenApi.aendern(a.id, { prioritaet });
        meldung.ok(`Priorität: ${PRIORITAETEN.find((p) => p.wert === prioritaet)?.label ?? prioritaet}`);
        return r.task;
      } catch (e) {
        meldung.fehler(e, "Priorität nicht gespeichert");
        return null;
      }
    },
    async zuweisen(a: Aufgabe, userId: string): Promise<Aufgabe | null> {
      try {
        const r = await aufgabenApi.aendern(a.id, { assigned_to: userId });
        return r.task;
      } catch (e) {
        meldung.fehler(e, "Nicht zugewiesen");
        return null;
      }
    },
  };
}

interface ZeileProps {
  aufgabe: Aufgabe;
  /** Lead-Name und Status zeigen (Inbox) */
  mitLead?: boolean;
  fokus?: boolean;
  onGeaendert: (a: Aufgabe) => void;
  onOeffnen?: () => void;
  /** Auswahlmodus der Inbox: Kästchen vor dem Kreis (Umschalt-Klick markiert einen Bereich) */
  auswahl?: { an: boolean; onUmschalten: (bereich: boolean) => void };
  /** Eigener Rückruf (z. B. Anruf ohne Lead: Nummern-Suche aufklappen). Ohne: die Telefonie wählt die Nummer am Lead. */
  onRueckruf?: () => void;
}

export function AufgabeZeile({ aufgabe: a, mitLead = false, fokus = false, onGeaendert, onOeffnen, auswahl, onRueckruf }: ZeileProps) {
  const { nutzer } = useAuth();
  const meta = useMeta();
  const aktionen = useAufgabenAktionen();
  const rueckruf = useRueckruf();
  const [bearbeiten, setBearbeiten] = useState(false);
  const [text, setText] = useState(a.text ?? "");
  const [datumOffen, setDatumOffen] = useState(false);
  const [verschiebenOffen, setVerschiebenOffen] = useState(false);
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  const zeile = useRef<HTMLDivElement>(null);
  const uhr = useRef<HTMLButtonElement>(null);
  const f = faelligkeit(a.date, a.due_at);
  const meins = a.assigned_to === nutzer?.id || a.assigned_to === nutzer?.close_user_id;
  const art = mitArt(a).art ?? "aufgabe";
  const system = istSystem(a); // Anruf, Voicemail, SMS, Opportunity fällig: nur Erledigen (bei Calls dazu der Rückruf)
  const text_anzeige = anzeigeText(a);
  const anrufbar = istAnrufbar(a);
  // Datum überall gleich: TT.MM.JJJJ (Design-Runde 2, K); überfällig steht rot
  const datumText = f.text;

  const ausfuehren = async (p: Promise<Aufgabe | null>) => {
    setBeschaeftigt(true);
    const neu = await p;
    setBeschaeftigt(false);
    if (neu) onGeaendert(neu);
  };

  return (
    <div
      ref={zeile}
      className={`aufgabe${a.is_complete ? " aufgabe--erledigt" : ""}${fokus ? " aufgabe--fokus" : ""}${f.ueberfaellig && !a.is_complete ? " aufgabe--ueberfaellig" : ""}${a.prioritaet === "hoch" ? " aufgabe--hoch" : ""}${a.prioritaet === "niedrig" ? " aufgabe--niedrig" : ""}${system ? ` aufgabe--system aufgabe--art-${art}` : ""}`}
      data-aufgabe={a.id}
      onClick={(e) => {
        if (!onOeffnen || bearbeiten) return;
        if ((e.target as HTMLElement).closest("button, a, input, textarea, label")) return;
        onOeffnen();
      }}
    >
      {auswahl ? (
        // Großes Klickfeld vom linken Zeilenrand bis zum Symbol: ein Klick knapp neben das Kästchen markiert auch
        // und öffnet nicht den Lead (Dilsar, 18.10.2026).
        <label
          className="aufgabe__auswahl-feld"
          title="Markieren (x)"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            auswahl.onUmschalten(e.shiftKey);
          }}
        >
          <input
            type="checkbox"
            className="aufgabe__auswahl"
            checked={auswahl.an}
            aria-label={`${a.lead_name ?? mitArt(a).phone_formatted ?? "Eintrag"} markieren`}
            onChange={() => undefined}
          />
        </label>
      ) : null}
      <button
        type="button"
        className={`aufgabe__haken aufgabe__haken--typ aufgabe__haken--art-${art}${a.is_complete ? " aufgabe__haken--an" : ""}`}
        title={a.is_complete ? "Wieder öffnen" : `${ART_LABEL[art]}: erledigt`}
        aria-label={a.is_complete ? "Wieder öffnen" : "Als erledigt markieren"}
        disabled={beschaeftigt}
        onClick={() => void ausfuehren(aktionen.erledigen(a, !a.is_complete))}
      >
        <TypSymbol art={art} erledigt={a.is_complete} />
      </button>
      <div className="aufgabe__inhalt">
        {mitLead ? <LeadZelle aufgabe={a} onOeffnen={onOeffnen} /> : null}
        {system ? (
          <span className="aufgabe__text aufgabe__text--system" title={text_anzeige}>
            {text_anzeige}
            {!mitLead && a.contact_name && a.contact_name !== a.lead_name ? <span className="aufgabe__kontakt"> · {a.contact_name}</span> : null}
          </span>
        ) : bearbeiten ? (
          <input
            className="eingabe eingabe--klein"
            value={text}
            autoFocus
            onChange={(e) => setText(e.target.value)}
            onBlur={() => {
              setBearbeiten(false);
              if (text.trim() && text.trim() !== a.text) void ausfuehren(aktionen.textSetzen(a, text.trim()));
              else setText(a.text ?? "");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
              if (e.key === "Escape") {
                setText(a.text ?? "");
                setBearbeiten(false);
              }
            }}
          />
        ) : (
          <button
            type="button"
            className="aufgabe__text"
            title={`${text_anzeige}${a.contact_name ? ` · ${a.contact_name}` : ""}${a.is_complete ? "" : "\n(Klicken zum Ändern)"}`}
            onClick={() => !a.is_complete && setBearbeiten(true)}
          >
            <PrioritaetMarke prioritaet={a.prioritaet} groesse={11} mitText />
            {text_anzeige}
            {a.contact_name && !mitLead && a.contact_name !== a.lead_name ? <span className="aufgabe__kontakt"> · {a.contact_name}</span> : null}
          </button>
        )}
      </div>
      <span className={`aufgabe__datum${f.ueberfaellig && !a.is_complete ? " aufgabe__datum--ueberfaellig" : ""}${f.heute ? " aufgabe__datum--heute" : ""}`} title={a.due_at ?? undefined}>
        {datumText}
      </span>
      {!meins && a.assigned_to ? (
        <Avatar name={a.assigned_to_name ?? meta.nutzerName(a.assigned_to)} id={a.assigned_to} groesse={20} titel={`Zugewiesen: ${a.assigned_to_name ?? meta.nutzerName(a.assigned_to)}`} />
      ) : mitLead ? (
        <span className="aufgabe__avatar-platz" aria-hidden="true" />
      ) : null}
      {anrufbar || (!a.is_complete && !system) || mitLead ? (
        <span className="aufgabe__aktionen">
          {anrufbar ? (
            <button
              type="button"
              className="symbolknopf rueckruf"
              title={`${istAnrufaufgabe(a) ? "Anrufen" : "Zurückrufen"}: ${telefonText(mitArt(a).phone, mitArt(a).phone_formatted)}`}
              aria-label={istAnrufaufgabe(a) ? "Anrufen" : "Zurückrufen"}
              onClick={() => (onRueckruf ? onRueckruf() : void rueckruf(a))}
            >
              <Icon name="telefon" groesse={14} />
            </button>
          ) : null}
          {!system && !a.is_complete ? (
            <>
              <button
                ref={uhr}
                type="button"
                className={`symbolknopf aufgabe__uhr${verschiebenOffen ? " symbolknopf--an" : ""}`}
                title="Verschieben (Datum, Uhrzeit)"
                aria-label="Verschieben"
                aria-expanded={verschiebenOffen}
                onClick={(e) => {
                  e.stopPropagation();
                  setVerschiebenOffen((o) => !o);
                }}
              >
                <Icon name="uhr" groesse={15} />
              </button>
              <VerschiebenPopover
                anker={uhr.current}
                offen={verschiebenOffen}
                aktuell={a.date}
                onSchliessen={() => setVerschiebenOffen(false)}
                onWahl={(date) => void ausfuehren(aktionen.verschiebenAuf(a, date))}
              />
              <MenueKnopf
                knopfKlasse="symbolknopf aufgabe__mehr"
                titel="Priorität, Zuweisen, Text ändern"
                ausrichtung="end"
                punkte={[
                  { label: "Priorität", ueberschrift: true },
                  ...PRIORITAETEN.map<MenuePunkt>((p) => ({
                    label: p.label,
                    icon: p.wert === "hoch" ? "pfeil-hoch" : p.wert === "niedrig" ? "pfeil-runter" : "pfeil-rechts",
                    rechts: (a.prioritaet ?? "normal") === p.wert ? "aktuell" : undefined,
                    deaktiviert: (a.prioritaet ?? "normal") === p.wert,
                    onClick: () => void ausfuehren(aktionen.prioritaetSetzen(a, p.wert)),
                  })),
                  { label: "Zuweisen an", ueberschrift: true, trenner: true },
                  ...meta.nutzerListe
                    .filter((u) => u.is_active)
                    .map<MenuePunkt>((u) => ({
                      label: u.name,
                      icon: "person",
                      deaktiviert: u.id === a.assigned_to || u.close_user_id === a.assigned_to,
                      onClick: () => void ausfuehren(aktionen.zuweisen(a, u.close_user_id ?? u.id)),
                    })),
                  { label: "Text ändern", icon: "stift", trenner: true, onClick: () => setBearbeiten(true) },
                  { label: "Datum und Uhrzeit wählen …", icon: "kalender", onClick: () => setDatumOffen(true) },
                ]}
              >
                <Icon name="mehr" groesse={15} />
              </MenueKnopf>
            </>
          ) : null}
        </span>
      ) : null}
      <DatumWahl
        anker={zeile.current}
        offen={datumOffen}
        startDatum={a.date ? (a.date.length > 10 ? berlinTeile(a.date).datum : a.date) : tagPlus(heute(), 1)}
        startZeit={a.date && a.date.length > 10 ? berlinTeile(a.date).zeit : ""}
        onSchliessen={() => setDatumOffen(false)}
        onWahl={(date) => void ausfuehren(aktionen.datumSetzen(a, date))}
      />
    </div>
  );
}

/** Datum und optionale Uhrzeit wählen. Ergebnis: YYYY-MM-DD oder ISO (wenn Uhrzeit gesetzt). */
export function DatumWahl({
  anker,
  offen,
  startDatum,
  startZeit = "",
  onSchliessen,
  onWahl,
}: {
  anker: HTMLElement | null;
  offen: boolean;
  startDatum: string;
  startZeit?: string;
  onSchliessen: () => void;
  onWahl: (date: string) => void;
}) {
  return (
    <Ausklapp anker={anker} offen={offen} onSchliessen={onSchliessen} ausrichtung="end" breite={280}>
      {offen ? <DatumFormular startDatum={startDatum} startZeit={startZeit} onAbbrechen={onSchliessen} onWahl={(d) => {
            onWahl(d);
            onSchliessen();
          }} /> : null}
    </Ausklapp>
  );
}

function DatumFormular({ startDatum, startZeit, onAbbrechen, onWahl }: { startDatum: string; startZeit: string; onAbbrechen: () => void; onWahl: (d: string) => void }) {
  const [datum, setDatum] = useState(startDatum);
  const [zeit, setZeit] = useState(startZeit);
  const [zeitFehler, setZeitFehler] = useState(false);
  return (
    <form
      className="datumwahl"
      onSubmit={(e) => {
        e.preventDefault();
        if (!datum || zeitFehler) return;
        onWahl(zeit ? berlinZuIso(datum, zeit) : datum);
      }}
    >
      <label className="feld">
        <span className="feld__label">Datum</span>
        <input className="eingabe" type="date" value={datum} autoFocus onClick={pickerOeffnen} onChange={(e) => setDatum(e.target.value)} required />
      </label>
      <label className="feld">
        <span className="feld__label">Uhrzeit (optional, dann mit Erinnerung)</span>
        <span className="zeile-eng">
          <ZeitFeld wert={zeit} onChange={setZeit} onUngueltig={setZeitFehler} />
          {zeit ? (
            <button type="button" className="knopf knopf--klein knopf--leise" onClick={() => setZeit("")}>
              Ohne Uhrzeit
            </button>
          ) : null}
        </span>
      </label>
      <div className="datumwahl__fuss">
        <button type="button" className="knopf knopf--klein knopf--leise" onClick={onAbbrechen}>
          Abbrechen
        </button>
        <button type="submit" className="knopf knopf--klein knopf--primaer">
          Übernehmen
        </button>
      </div>
    </form>
  );
}

interface FollowUpProps {
  leadId: string;
  kontakte: Kontakt[];
  onAngelegt: (a: Aufgabe) => void;
  klein?: boolean;
  /** schlankes Symbol statt Knopf mit Text (Lead-Kopf) */
  symbol?: boolean;
}

/** Art der neuen Aufgabe: Follow-up (Standard) oder Anrufaufgabe (Close „Call“, `typ: "outgoing_call"`) */
export type NeueAufgabeTyp = "lead" | "outgoing_call";

/** Eingabe für eine neue Aufgabe; bei einer Anrufaufgabe ist der Text optional („Anrufen: Name“). */
export type NeueAufgabe = Omit<AufgabeEingabe, "text"> & { text?: string; typ?: NeueAufgabeTyp };

/** Follow-up oder Anrufaufgabe anlegen mit Meldung; liefert true, wenn es geklappt hat. */
export function useFollowUpAnlegen(onAngelegt: (a: Aufgabe) => void) {
  const meldung = useMeldung();
  const [laeuft, setLaeuft] = useState(false);
  const anlegen = async (eingabe: NeueAufgabe) => {
    setLaeuft(true);
    const anruf = eingabe.typ === "outgoing_call";
    try {
      const r = await aufgabenApi.anlegen(eingabe as AufgabeEingabe);
      onAngelegt(r.task);
      const tag = eingabe.date ? (eingabe.date.length > 10 ? berlinTeile(eingabe.date).datum : eingabe.date) : r.task.date?.slice(0, 10) ?? null;
      const uhrzeit = eingabe.date && eingabe.date.length > 10 ? `, ${berlinTeile(eingabe.date).zeit} Uhr` : "";
      const prio = eingabe.prioritaet === "hoch" ? " (hohe Priorität)" : eingabe.prioritaet === "niedrig" ? " (niedrige Priorität)" : "";
      meldung.ok(`${anruf ? "Anrufaufgabe" : "Follow-up"} ${tag ? `für ${tagText(tag)}${uhrzeit}` : "ohne Datum"}${prio} angelegt.`);
      return true;
    } catch (e) {
      meldung.fehler(e, anruf ? "Anrufaufgabe nicht angelegt" : "Follow-up nicht angelegt");
      return false;
    } finally {
      setLaeuft(false);
    }
  };
  return { anlegen, laeuft };
}

/** „+ Follow-up“: ein Klick legt „Follow-up“ für morgen an (ohne Uhrzeit). Pfeil: Text, Priorität, Tag, Uhrzeit, Nutzer, Kontakt. */
export function FollowUpKnopf({ leadId, kontakte, onAngelegt, klein = false, symbol = false }: FollowUpProps) {
  const [offen, setOffen] = useState(false);
  const { anlegen, laeuft } = useFollowUpAnlegen(onAngelegt);
  const gruppe = useRef<HTMLDivElement>(null);

  return (
    <div className={`knopfgruppe${symbol ? " knopfgruppe--symbol" : ""}`} ref={gruppe}>
      <button
        type="button"
        className={symbol ? "symbolknopf ls-aktion" : `knopf${klein ? " knopf--klein" : ""}`}
        disabled={laeuft}
        title="Follow-up für morgen anlegen (f)"
        aria-label="Follow-up für morgen anlegen"
        data-follow-up=""
        onClick={() => void anlegen({ lead_id: leadId, text: "Follow-up", date: tagPlus(heute(), 1) })}
      >
        {symbol ? <Icon name="aufgabe" groesse={16} /> : <Icon name="plus" groesse={14} />}
        {symbol ? null : "Follow-up"}
      </button>
      <button
        type="button"
        className={symbol ? "symbolknopf ls-aktion ls-aktion--pfeil" : `knopf knopf--pfeil${klein ? " knopf--klein" : ""}`}
        aria-label="Follow-up mit Priorität, Datum, Uhrzeit und Text"
        title="Priorität, Datum, Uhrzeit, Text wählen"
        onClick={() => setOffen((o) => !o)}
      >
        <Icon name="chevron-unten" groesse={14} />
      </button>
      <Ausklapp anker={gruppe.current} offen={offen} onSchliessen={() => setOffen(false)} ausrichtung="end" breite={320} maxHoehe={540}>
        {offen ? (
          <FollowUpFormular
            kontakte={kontakte}
            laeuft={laeuft}
            onAbbrechen={() => setOffen(false)}
            onAnlegen={async (e) => {
              if (await anlegen({ ...e, lead_id: leadId })) setOffen(false);
            }}
          />
        ) : null}
      </Ausklapp>
    </div>
  );
}

/** Formular für ein neues Follow-up wie Close „Aufgabe anlegen“ (Pfeil am Knopf „Follow-up“ im Lead-Kopf und „+“ im Abschnitt
 * Follow-ups der Lead-Seite): Textfeld (Platzhalter „Follow-up“, leer), darunter in einer Zeile Tag, Uhrzeit (optional),
 * Dringlichkeit, Zuständig. Enter legt an, Esc bricht ab, kein Bestätigungsdialog. Tag leer = morgen (Anruf: heute).
 * Nichts gewählt bei Zuständig = der eingeloggte Nutzer. Reiter „Follow-up | Anruf“ wie „Task | Call“ in Close. */
export function FollowUpFormular({
  kontakte,
  laeuft,
  onAbbrechen,
  onAnlegen,
  startPrioritaet = "normal",
}: {
  kontakte: Kontakt[];
  laeuft: boolean;
  onAbbrechen: () => void;
  onAnlegen: (e: Omit<NeueAufgabe, "lead_id">) => void;
  startPrioritaet?: Prioritaet;
}) {
  const { nutzer } = useAuth();
  const meta = useMeta();
  const h = heute();
  const [typ, setTyp] = useState<NeueAufgabeTyp>("lead");
  const [text, setText] = useState("");
  const [prioritaet, setPrioritaet] = useState<Prioritaet>(startPrioritaet);
  const [datum, setDatum] = useState("");
  const [datumFokus, setDatumFokus] = useState(false);
  const [zeit, setZeit] = useState("");
  const [zeitFehler, setZeitFehler] = useState(false);
  const [wer, setWer] = useState<string>(nutzer?.close_user_id ?? nutzer?.id ?? "");
  const [kontakt, setKontakt] = useState<string | null>(null);
  const [kontaktOffen, setKontaktOffen] = useState(false);
  const anruf = typ === "outgoing_call";
  const standardTag = anruf ? h : tagPlus(h, 1);
  const tag = datum || standardTag;
  return (
    <form
      className="followup followup--close"
      data-follow-up-formular=""
      onSubmit={(e) => {
        e.preventDefault();
        if (zeitFehler || laeuft) return; // ungültige Uhrzeit bleibt rot stehen
        onAnlegen({
          text: anruf ? text.trim() || undefined : text.trim() || "Follow-up",
          date: zeit ? berlinZuIso(tag, zeit) : tag,
          assigned_to: wer || null,
          contact_id: kontakt,
          prioritaet,
          typ: anruf ? "outgoing_call" : undefined,
        });
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          onAbbrechen();
        }
      }}
    >
      <div className="aufgabenart" role="tablist" aria-label="Art">
        <button type="button" role="tab" aria-selected={!anruf} className={`aufgabenart__reiter${!anruf ? " aufgabenart__reiter--an" : ""}`} onClick={() => setTyp("lead")}>
          Follow-up
        </button>
        <button type="button" role="tab" aria-selected={anruf} className={`aufgabenart__reiter${anruf ? " aufgabenart__reiter--an" : ""}`} title="Anrufaufgabe: erledigt sich nach dem Anruf" onClick={() => setTyp("outgoing_call")}>
          Anruf
        </button>
      </div>
      <input
        className="eingabe followup__text"
        value={text}
        autoFocus
        aria-label={anruf ? "Text der Anrufaufgabe" : "Text des Follow-ups"}
        placeholder={anruf ? "Anrufen: Name" : "Follow-up"}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="followup__zeile">
        <span className={`followup__tag${!datum && !datumFokus ? " followup__tag--leer" : ""}`}>
          <input
            className="eingabe"
            type="date"
            value={datum}
            aria-label="Tag"
            onClick={pickerOeffnen}
            onFocus={() => setDatumFokus(true)}
            onBlur={() => setDatumFokus(false)}
            onChange={(e) => setDatum(e.target.value)}
          />
          {!datum && !datumFokus ? <span className="followup__platzhalter">{anruf ? "Heute" : "Morgen"}</span> : null}
        </span>
        <ZeitFeld wert={zeit} onChange={setZeit} onUngueltig={setZeitFehler} ariaLabel="Uhrzeit (optional)" title="Uhrzeit nur wenn gewünscht (dann mit Erinnerung), z. B. 15 oder 15:30" />
        <Auswahl
          optionen={PRIORITAETEN.map((p) => ({ wert: p.wert, label: p.label }))}
          wert={prioritaet}
          onWahl={(w) => setPrioritaet((w as Prioritaet | null) ?? "normal")}
          titel="Dringlichkeit"
        />
        <Auswahl
          optionen={meta.nutzerListe.filter((u) => u.is_active).map((u) => ({ wert: u.close_user_id ?? u.id, label: u.name }))}
          wert={wer}
          onWahl={(w) => setWer(String(w ?? ""))}
          titel="Zuständig"
          platzhalter="Zuständig"
        />
      </div>
      {kontakte.length && (anruf || kontaktOffen || kontakt) ? (
        <Auswahl optionen={kontakte.map((k) => ({ wert: k.id, label: k.name ?? "Ohne Namen", unter: k.title }))} wert={kontakt} leerErlaubt platzhalter={anruf ? "Kontakt (leer: erster mit Nummer)" : "Kein Kontakt"} onWahl={(w) => setKontakt((w as string | null) ?? null)} />
      ) : kontakte.length ? (
        <button type="button" className="textknopf followup__kontakt" onClick={() => setKontaktOffen(true)}>
          + Kontakt
        </button>
      ) : null}
      <div className="datumwahl__fuss">
        <button type="button" className="knopf knopf--klein knopf--leise" onClick={onAbbrechen}>
          Abbrechen
        </button>
        <button type="submit" className="knopf knopf--klein knopf--primaer" disabled={laeuft}>
          {laeuft ? "Wird angelegt …" : anruf ? "Anrufaufgabe anlegen" : "Follow-up anlegen"}
        </button>
      </div>
    </form>
  );
}
